import React, { useEffect, useRef } from 'react';
import { cx } from '@emotion/css';
import { useUniqueId } from '../utils/useUniqueID';
import { useDescribers } from '../utils/useDescribers';
import { Help } from '../InputModules/Help';
import { InputBottomRow } from '../InputModules/InputBottomRow';
import { ComboboxInput, ListboxPortal } from '../SelectModules';
import { determineItemValue, setupSafelyCallItemToID, useDetermineFilteredListItems, useSingleSelectHooks, } from '../SelectModules/utils';
import { selectInputContainerStyles, inputContainerStyles, inputTopRowStyles } from '../shared';
import { validateInputLabelAccessibility } from '../utils/helperFunctions';
export const SingleSelect = (props) => {
    const { allowItemTextWrap, 'aria-label': ariaLabel, className, customRender, error, guidance, help, id, inputHeight, initialSelectedItem, innerInputRef, innerWrapperRef, isAsync, isDisabled, isLoading, isRequired, itemIsAnchored, itemIsDisabled, items = [], itemToID, itemToString, label, listboxWidth, maxHeight, onBlur, onChange, onInputChange, onInternalStateChange, onListboxScroll, placeholder, shouldShowAdditionalResultsMessage, testId, units, value, ...restProps } = props;
    validateInputLabelAccessibility('SingleSelect', label, ariaLabel);
    const dropdownWrapperRef = useRef(null);
    const singleSelectInnerInputRef = useRef(null);
    const selectInputSingleSelectRef = innerInputRef || singleSelectInnerInputRef;
    const uniqueIdSingleSelect = useUniqueId(id);
    const describersSingleSelect = useDescribers({ help, uniqueId: uniqueIdSingleSelect, guidance });
    const renderItemToStringSingleSelect = itemToString || determineItemValue;
    const { filteredAnchoredItemsCount, listItems, updateFilteredListboxItems } = useDetermineFilteredListItems();
    const updateFilteredItemsSingleSelect = (inputText = inputValue, currentlySelected = selectedItem) => {
        updateFilteredListboxItems({
            itemIsAnchored,
            items,
            itemToID,
            renderItemToString: renderItemToStringSingleSelect,
        }, inputText, currentlySelected);
    };
    const clearSelection = () => {
        selectItem(null);
    };
    const safelyCallItemToID = (item) => {
        return setupSafelyCallItemToID(itemToID, renderItemToStringSingleSelect, item);
    };
    const { useComboboxControlProps } = useSingleSelectHooks({
        initialSelectedItem,
        isDisabled,
        itemIsDisabled,
        listItems,
        onChange,
        onInputChange,
        onInternalStateChange,
        renderItemToString: renderItemToStringSingleSelect,
        selectInput: selectInputSingleSelectRef,
        updateFilteredItems: updateFilteredItemsSingleSelect,
    });
    const { getInputProps, getItemProps, getLabelProps, getMenuProps, getToggleButtonProps, highlightedIndex, inputValue, isOpen, openMenu, selectedItem, selectItem, } = useComboboxControlProps;
    // Update items on initial render and when `items` or `selectedItem` change
    useEffect(() => {
        if (items.length) {
            updateFilteredItemsSingleSelect(inputValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [items, selectedItem]);
    useEffect(() => {
        // re-select current item to cause it to update when items changes
        // since the item's rendered string might have updated e.g. it contains a count
        if (items.length && selectedItem && itemToID && !isAsync) {
            const updatedItem = items.find((it) => itemToID(it) === itemToID(selectedItem));
            // ensure it still exists in new items list
            if (updatedItem !== selectedItem) {
                selectItem(updatedItem);
            }
        }
    }, [items, selectedItem, itemToID, selectItem, isAsync]);
    // If value prop changes, update selection (as long as value and selected item differ)
    useEffect(() => {
        const valueProvided = Object.hasOwn(props, 'value');
        if (valueProvided) {
            if (typeof value === 'object' && typeof selectedItem === 'object') {
                if (safelyCallItemToID(value) !== safelyCallItemToID(selectedItem)) {
                    selectItem(value);
                }
            }
            else if (typeof value === 'string' && typeof selectedItem === 'string') {
                if (value !== selectedItem) {
                    selectItem(value);
                }
            }
            else {
                selectItem(value);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value]);
    return (React.createElement("div", { className: cx(inputContainerStyles, selectInputContainerStyles, 'ds-single-select'), "data-testid": `${testId}-container`, ...restProps, ref: innerWrapperRef },
        React.createElement("div", { className: inputTopRowStyles, "data-testid": `${testId}-top-row` },
            React.createElement(Help, { id: uniqueIdSingleSelect, testId: testId || uniqueIdSingleSelect, ...help })),
        React.createElement(ComboboxInput, { ariaLabel: ariaLabel, className: className, clearSelection: clearSelection, dropdownWrapperRef: dropdownWrapperRef, describers: describersSingleSelect, error: error, getInputProps: getInputProps, getLabelProps: getLabelProps, getToggleButtonProps: getToggleButtonProps, inputHeight: inputHeight, inputValue: inputValue, isDisabled: isDisabled, isOpen: isOpen, isRequired: isRequired, itemToString: renderItemToStringSingleSelect, label: label, onBlur: onBlur, openMenu: openMenu, placeholder: placeholder, selectedItem: selectedItem, selectInput: selectInputSingleSelectRef, testId: testId || uniqueIdSingleSelect, uniqueId: uniqueIdSingleSelect }),
        React.createElement(ListboxPortal, { allowItemTextWrap: allowItemTextWrap, dropdownWrapperRef: dropdownWrapperRef, customRender: customRender, filteredAnchoredItemsCount: filteredAnchoredItemsCount, filteredItems: listItems, getItemProps: getItemProps, getMenuProps: getMenuProps, highlightedIndex: highlightedIndex, id: uniqueIdSingleSelect, inputValue: inputValue || '', isAsync: isAsync, isDisabled: isDisabled, isLoading: isLoading, isOpen: isOpen, itemIsDisabled: itemIsDisabled, itemToID: itemToID, itemToString: renderItemToStringSingleSelect, listboxWidth: listboxWidth, maxHeight: maxHeight, onListboxScroll: onListboxScroll, selectedItem: selectedItem, shouldShowAdditionalResultsMessage: shouldShowAdditionalResultsMessage, testId: testId || uniqueIdSingleSelect, totalItemCount: items.length }),
        React.createElement(InputBottomRow, { error: error, guidance: guidance, id: uniqueIdSingleSelect, isDisabled: isDisabled, testId: testId || uniqueIdSingleSelect, units: units })));
};
SingleSelect.displayName = 'SingleSelect';
